import React from 'react';

export interface ViewfinderProps {
  stream?: MediaStream | null;
  facingMode?: 'user' | 'environment';
  onLoadedMetadata?: (video: HTMLVideoElement) => void;
  className?: string;
  overlay?: React.ReactNode;
  corners?: boolean;
  horizon?: boolean;
  aspectRatio?: string;
}

export const Viewfinder: React.FC<ViewfinderProps> = ({
  stream,
  // facingMode is used by the consumer to select camera before creating stream
  onLoadedMetadata,
  className = '',
  overlay,
  corners = true,
  horizon = true,
  aspectRatio = '16/10',
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (video && stream) {
      video.srcObject = stream;
      video.play().catch(console.error);
    }
    return () => {
      if (video && video.srcObject) {
        const tracks = (video.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        video.srcObject = null;
      }
    };
  }, [stream]);

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    onLoadedMetadata?.(e.currentTarget);
  };

  return (
    <div
      className={`viewfinder ${className}`}
      style={{ aspectRatio } as React.CSSProperties}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onLoadedMetadata={handleLoadedMetadata}
        className="w-full h-full object-cover"
      />
      {corners && (
        <div className="viewfinder-overlay">
          <div className="flex justify-between">
            <div className="viewfinder-corner viewfinder-corner-tl" />
            <div className="viewfinder-corner viewfinder-corner-tr" />
          </div>
          {horizon && <div className="viewfinder-horizon" />}
          <div className="flex justify-between">
            <div className="viewfinder-corner viewfinder-corner-bl" />
            <div className="viewfinder-corner viewfinder-corner-br" />
          </div>
        </div>
      )}
      {overlay}
    </div>
  );
};

export interface CaptureButtonProps {
  onCapture: () => void;
  disabled?: boolean;
  isCapturing?: boolean;
  className?: string;
  size?: number;
}

export const CaptureButton: React.FC<CaptureButtonProps> = ({
  onCapture,
  disabled = false,
  isCapturing = false,
  className = '',
  size = 72,
}) => {
  return (
    <button
      type="button"
      onClick={onCapture}
      disabled={disabled || isCapturing}
      className={`
        relative flex items-center justify-center rounded-full
        border-4 border-surface-container-lowest
        bg-surface-container-lowest/80
        backdrop-blur-md
        transition-all duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      style={{ width: size, height: size } as React.CSSProperties}
      aria-label={isCapturing ? 'Capturando...' : 'Capturar foto'}
    >
      <span
        className={`
          rounded-full bg-on-surface transition-all duration-150
          ${isCapturing ? 'animate-pulse-soft scale-75' : 'scale-100'}
        `}
        style={{
          width: size * 0.65,
          height: size * 0.65,
        } as React.CSSProperties}
      />
      {isCapturing && (
        <span className="absolute inset-0 rounded-full border-2 border-primary animate-spin-slow" />
      )}
    </button>
  );
};

export interface FlipCameraButtonProps {
  onFlip: () => void;
  disabled?: boolean;
  className?: string;
}

export const FlipCameraButton: React.FC<FlipCameraButtonProps> = ({
  onFlip,
  disabled = false,
  className = '',
}) => {
  return (
    <button
      type="button"
      onClick={onFlip}
      disabled={disabled}
      className={`
        btn btn-icon
        ${className}
      `}
      aria-label="Alternar câmera"
    >
      <span className="material-symbols-outlined text-lg">flip_camera_ios</span>
    </button>
  );
};

export interface GalleryButtonProps {
  onOpen: () => void;
  disabled?: boolean;
  thumbnail?: string | null;
  className?: string;
}

export const GalleryButton: React.FC<GalleryButtonProps> = ({
  onOpen,
  disabled = false,
  thumbnail,
  className = '',
}) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className={`
        relative btn btn-icon overflow-hidden
        ${className}
      `}
      aria-label="Abrir galeria"
    >
      {thumbnail ? (
        <img
          src={thumbnail}
          alt="Última foto"
          className="w-full h-full object-cover rounded-xl"
        />
      ) : (
        <span className="material-symbols-outlined text-lg">photo_library</span>
      )}
    </button>
  );
};